/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import bcrypt from "bcryptjs";
import cn from "classnames";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import openToast from "utils/openToast";

import { resetPasswordApi } from "lib/v3/loginAPI";
import { useAuth } from "state";

import HidePasswordIcon from "components/icons/HidePasswordIcon";
import ShowPasswordIcon from "components/icons/ShowPasswordIcon";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import Label from "components/ui/Label";
import { Input } from "components/ui/Input";
import { Button } from "components/ui/Button";

import styles from "../../../style.module.css";

import PasswordValidator, { checkPassword } from "./PasswordValidator";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";

interface IFormInput {
  password: string;
  repeatedPassword: string;
}

export interface ResetPasswordRequest {
  password: string;
  password_confirmation: string;
}

const ResetPassword = ({
  username,
  setCurrentForm,
}: {
  username: string;
  setCurrentForm: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRepeatedPasswordShown, setIsRepeatedPasswordShown] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IFormInput>({
    defaultValues: {
      password: "",
      repeatedPassword: "",
    },
  });

  const password = watch("password");
  const repeatedPassword = watch("repeatedPassword");
  const navigate = useNavigate();
  const { handleLogin } = useAuth();

  const { mutate: resetPassword } = useMutation({
    mutationFn: (data: ResetPasswordRequest) => resetPasswordApi(data),
    onSuccess: (res) => {
      const { token, reactapp_v, setting_id, step } = res.data;
      localStorage.setItem("token", token);

      if (token) {
        localStorage.setItem("role", "admin");
      }

      if (step === "dashboard") {
        if (reactapp_v) {
          localStorage.setItem("version", reactapp_v);
        }

        if (setting_id) {
          localStorage.setItem("setting_id", setting_id);
        }

        handleLogin(res.data, "admin");
        navigate("/");
        return;
      }

      setCurrentForm(step);
    },
    onError: (error: any) => {
      setIsLoading(false);
      openToast(error.response.data.error, "error");
    },
  });

  const PASSWORD_SALT = useEnvs((state) => state.password_salt);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    setIsLoading(true);

    const hashPassword = bcrypt.hashSync(data.password, PASSWORD_SALT);

    const hashRepeatedPassword = bcrypt.hashSync(
      data.repeatedPassword,
      PASSWORD_SALT,
    );

    if (hashPassword !== hashRepeatedPassword) {
      openToast("Password confirmation doesn't match the Password.", "error");
      setIsLoading(false);
      return;
    }

    resetPassword({
      password: hashPassword,
      password_confirmation: hashRepeatedPassword,
    });
  };

  const isPasswordValid = Object.values(checkPassword(password)).every(
    (value) => value === true,
  );

  const isDisabled =
    isLoading ||
    !isPasswordValid ||
    (password === "" && repeatedPassword === "") ||
    password !== repeatedPassword;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack className="space-y-12">
        <Stack>
          <Stack className="space-y-1 mb-8">
            <h3 className="text-white text-lg md:text-xl font-bold">
              Create new password
            </h3>
            <Text className="text-sm text-[#9A9A9A]">
              For security reasons, please set a unique strong password for your
              account <span className="font-bold text-[#fff]">{username}</span>
            </Text>
          </Stack>

          <Stack className="relative mb-5">
            <Label
              className="text-base font-bold text-gray-200"
              isRequired
              variant={"dark"}
              htmlFor="password"
            >
              New Password
            </Label>

            <PasswordValidator
              password={password}
              register={register("password")}
              hasSpecialChar
            />
          </Stack>

          <Stack>
            <Label
              htmlFor="repeatedPassword"
              className="text-base font-bold text-gray-200"
              isRequired
              variant={"dark"}
            >
              Repeat Password
            </Label>

            <div className="relative">
              <Input
                type={isRepeatedPasswordShown ? "text" : "password"}
                id="repeatedPassword"
                className="border-[#535353] block w-full outline-none p-4 focus:ring-0 text-base"
                placeholder="**************"
                {...register("repeatedPassword")}
              />

              <button
                type="button"
                className="absolute flex right-3 items-center bg-secondary-accent h-fit top-1/2 transform -translate-y-1/2 rounded-full"
                onClick={() =>
                  setIsRepeatedPasswordShown(!isRepeatedPasswordShown)
                }
              >
                {isRepeatedPasswordShown ? (
                  <HidePasswordIcon />
                ) : (
                  <ShowPasswordIcon />
                )}
              </button>
            </div>

            {errors.repeatedPassword && (
              <Text>
                Invalid login information, please check and try again.
              </Text>
            )}
          </Stack>
        </Stack>

        <div>
          <Button
            variant="primary"
            type="submit"
            size="xl"
            shouldPreventDefault={false}
            isLoading={isLoading}
            className={cn({
              [styles.disabled]: isDisabled,
            })}
            disabled={isDisabled}
            id="loginBtn"
          >
            Set New Password
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export default ResetPassword;
